import { FC, useEffect, useState, useCallback } from "react";
import {
  Button,
  color,
  InputGroup,
  InputSelect,
  NAME_REGEX,
  NO_REGEX,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { Industry, LegalStructure, IndustryGroup } from "../../constants/enums";
import {
  IndustryGroupMap,
  LegalStructures,
} from "../../constants/dropdownValues";
import { camelCaseToNormalCase } from "../../Utils/formatter";
import { toast } from "react-toastify";
import { useStyles } from "../../styles/BizProfileCss";
import { countries } from "@funded-here-interface/common/src/constant/country";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { initializeBusinessDetails } from "../../Utils/dispatchISPV";

const initialFormState = {
  firstName: "",
  lastName: "",
  companyName: "",
  registrationNumber: "",
  legalStructure: "",
  legalStructureOthers: "",
  industryGroup: "",
  industryDropdownValues: [] as Industry[],
  institutionType: "",
  industryOthers: "",
  country: "",
  address: "",
  postalcode: "",
};

const initialErrorState = Object.keys(initialFormState).reduce((acc, key) => {
  acc[key] = "";
  return acc;
}, {} as Record<string, string>);

const DistributorBizProfile: FC = () => {
  const { classes } = useStyles();
  const Navigate = useNavigate();
  const dispatch = useDispatch();

  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [form, setForm] = useState({ ...initialFormState });
  const [error, setError] = useState({ ...initialErrorState });
  const [isOtherLegalStructure, setIsOtherLegalStructure] = useState(false);
  const [initPage, setInitPage] = useState(true);

  // Populate form from draft
  const populateFormFromDraft = useCallback((data: any) => {
    setForm({
      firstName: data.userFirstName || "",
      lastName: data.userLastName || "",
      companyName: data.companyName || "",
      registrationNumber: data.companyRegistrationNo || "",
      legalStructure: data.legalStructure || "",
      legalStructureOthers: data.legalStructureOthers || "",
      industryGroup: data.industryGroup || "",
      industryDropdownValues:
        IndustryGroupMap[data.industryGroup as IndustryGroup] || [],
      institutionType: data.industry || "",
      industryOthers: data.industryOthers || "",
      country: data.country || "",
      address: data.address || "",
      postalcode: data.postalCode?.toString() || "",
    });
    setIsOtherLegalStructure(data.legalStructure === LegalStructure.OTHERS);
  }, []);

  // Fetch draft on mount
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await getOrgDraftMutation.mutateAsync({
            userId: parseInt(userId),
            token,
          });
          if (data) {
            populateFormFromDraft(data);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };
    initializeDraft();
  }, []);

  // Generic change handler
  const handleFieldChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  // Special handlers
  const handleLegalStructureChange = (value: LegalStructure) => {
    setForm((prev) => ({
      ...prev,
      legalStructure: value,
      legalStructureOthers: "",
    }));
    setIsOtherLegalStructure(value === LegalStructure.OTHERS);
    setError((prev) => ({
      ...prev,
      legalStructure: "",
      legalStructureOthers: "",
    }));
  };

  const handleIndustryGroupChange = (value: IndustryGroup) => {
    setForm((prev) => ({
      ...prev,
      industryGroup: value,
      industryDropdownValues: IndustryGroupMap[value] || [],
      institutionType: "",
    }));
    setError((prev) => ({
      ...prev,
      industryGroup: "",
      institutionType: "",
    }));
  };

  // Validation
  const validateForm = () => {
    let valid = true;
    const customError: typeof error = { ...initialErrorState };

    // Required fields except custom ones
    Object.entries(form).forEach(([key, value]) => {
      if (
        key !== "legalStructureOthers" &&
        key !== "institutionType" &&
        key !== "industryOthers" &&
        key !== "industryDropdownValues" &&
        !value
      ) {
        valid = false;
        customError[key] = `${camelCaseToNormalCase(key)} is required`;
      }
    });

    if (isOtherLegalStructure && !form.legalStructureOthers) {
      valid = false;
      customError.legalStructureOthers =
        "Legal Structure Specification is required";
    }
    if (!form.institutionType) {
      valid = false;
      customError.institutionType = "Industry is required";
    }
    setError(customError);
    return valid;
  };

  // Save draft
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        userFirstName: form.firstName,
        userLastName: form.lastName,
        companyName: form.companyName,
        companyRegistrationNo: form.registrationNumber,
        legalStructure: form.legalStructure,
        industryGroup: form.industryGroup,
        industry: form.institutionType,
        country: form.country,
        address: form.address,
        postalCode: form.postalcode,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Submit handler
  const handleSubmit = () => {
    if (validateForm()) {
      saveDraft(false);
      initializeBusinessDetails(dispatch, form);

      setTimeout(() => {
        Navigate("/issuing-spv-bank-details");
      }, 500);
    }
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <InputGroup
        isErrored={error.firstName}
        onChange={(value: string) =>
          handleFieldChange("firstName", value.replace(NAME_REGEX, ""))
        }
        labelText="First Name"
        id="firstName"
        value={form.firstName}
      />
      <InputGroup
        isErrored={error.lastName}
        onChange={(value: string) =>
          handleFieldChange("lastName", value.replace(NAME_REGEX, ""))
        }
        labelText="Last Name"
        id="lastName"
        value={form.lastName}
      />
      <InputGroup
        isErrored={error.companyName}
        onChange={(value: string) => handleFieldChange("companyName", value)}
        labelText="Company Name"
        id="company"
        value={form.companyName}
      />
      <InputGroup
        isErrored={error.registrationNumber}
        onChange={(value: string) =>
          handleFieldChange("registrationNumber", value)
        }
        labelText="Registration Number"
        id="registrationNumber"
        value={form.registrationNumber}
      />
      <InputSelect
        isErrored={error.legalStructure}
        onChange={handleLegalStructureChange}
        labelText="Legal Structure"
        id="legalStructure"
        value={form.legalStructure}
        data={LegalStructures}
      />
      {isOtherLegalStructure && (
        <InputGroup
          isErrored={error.legalStructureOthers}
          onChange={(value: string) =>
            handleFieldChange("legalStructureOthers", value)
          }
          labelText="Please Specify Legal Structure"
          id="legalStructureOthers"
          value={form.legalStructureOthers}
        />
      )}
      <InputSelect
        isErrored={error.industryGroup}
        onChange={handleIndustryGroupChange}
        labelText="Industry Group"
        value={form.industryGroup}
        id="industryGroup"
        data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
      />
      <InputSelect
        isErrored={error.institutionType}
        onChange={(value: Industry) =>
          handleFieldChange("institutionType", value)
        }
        labelText="Industry"
        value={form.institutionType}
        id="institutionType"
        data={[...form.industryDropdownValues].sort((a, b) =>
          a.localeCompare(b)
        )}
      />
      <InputSelect
        isErrored={error.country}
        onChange={(value: string) => handleFieldChange("country", value)}
        labelText="Country"
        id="country"
        value={form.country}
        data={countries}
      />
      <InputGroup
        isErrored={error.address}
        onChange={(value: string) => handleFieldChange("address", value)}
        labelText="Address"
        id="address"
        value={form.address}
      />
      <InputGroup
        isErrored={error.postalcode}
        onChange={(value: string) =>
          handleFieldChange("postalcode", value.replace(NO_REGEX, ""))
        }
        labelText="Postal Code"
        id="postalcode"
        value={form.postalcode}
      />
      <Button
        onClick={handleSubmit}
        textColor="#FFFF"
        backgroundColor="#338F86"
        width="100%"
      >
        Next
      </Button>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft()}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          width="100%"
        >
          Save Draft
        </Button>
      </div>
    </div>
  );
};

export default DistributorBizProfile;
